import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Button,
  EmptyState,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import {
  useCreateVariantMutation,
  useDeleteVariantMutation,
  useSetVariantStatusMutation,
  useVariantsQuery,
} from '../api';
import { formatCatalogPrice } from '../format-price';
import type { VariantFormValues } from '../schemas';

import { VariantForm } from './VariantForm';
import { VariantStatusBadge } from './VariantStatusBadge';

import { useToast } from '@/components/ui/toast/ToastProvider';

export function VariantList({ productId }: { productId: string }): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const variantsQuery = useVariantsQuery(productId);
  const createMutation = useCreateVariantMutation(productId);
  const deleteMutation = useDeleteVariantMutation(productId);
  const statusMutation = useSetVariantStatusMutation(productId);
  const [modalOpen, setModalOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function onCreate(values: VariantFormValues): Promise<void> {
    setFormError(null);
    try {
      const variant = await createMutation.mutateAsync(values);
      toast.success(t('merchant.products.variants.createdToast', { sku: variant.sku }));
      setModalOpen(false);
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  async function onDelete(variantId: string): Promise<void> {
    if (!window.confirm(t('merchant.products.variants.deleteConfirm'))) return;
    try {
      await deleteMutation.mutateAsync(variantId);
      toast.success(t('merchant.products.variants.deletedToast'));
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  /** The reversible default: activate/deactivate, never DELETE — see `ProductVariantRepository.setActive()`'s own header. */
  async function onToggleStatus(variantId: string, nextIsActive: boolean): Promise<void> {
    try {
      await statusMutation.mutateAsync({ variantId, isActive: nextIsActive });
      toast.success(
        t(
          nextIsActive
            ? 'merchant.products.variants.activatedToast'
            : 'merchant.products.variants.deactivatedToast',
        ),
      );
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold text-ink">
          {t('merchant.products.variants.title')}
        </h2>
        <Button
          size="sm"
          onClick={() => {
            setFormError(null);
            setModalOpen(true);
          }}
        >
          {t('merchant.products.variants.add')}
        </Button>
      </div>

      {variantsQuery.isPending ? (
        <p className="text-sm text-inkFaint">{t('merchant.common.loadingAria')}…</p>
      ) : variantsQuery.isError ? (
        <EmptyState
          title={t('merchant.products.variants.loadError')}
          description={t(resolveErrorMessageKey(variantsQuery.error))}
        />
      ) : variantsQuery.data.length === 0 ? (
        <EmptyState title={t('merchant.products.variants.empty')} />
      ) : (
        <Table caption={t('merchant.products.variants.title')}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('merchant.products.form.skuLabel')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.products.variants.colAttributes')}</TableHeaderCell>
              <TableHeaderCell align="end">
                {t('merchant.products.variants.colSpecificPrice')}
              </TableHeaderCell>
              <TableHeaderCell>{t('merchant.products.variants.colStatus')}</TableHeaderCell>
              <TableHeaderCell> </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {variantsQuery.data.map((variant) => (
              <TableRow key={variant.id}>
                <TableCell>
                  <span className="font-mono text-xs text-inkSoft">{variant.sku}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-inkSoft">
                    {Object.entries(variant.attributes)
                      .map(([key, value]) => `${key}: ${String(value)}`)
                      .join(', ') || '—'}
                  </span>
                </TableCell>
                <TableCell align="end">
                  <span className="num text-sm text-ink">
                    {variant.priceOverride !== null
                      ? formatCatalogPrice(variant.priceOverride)
                      : '—'}
                  </span>
                </TableCell>
                <TableCell>
                  <VariantStatusBadge isActive={variant.isActive} />
                </TableCell>
                <TableCell align="end">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={
                        statusMutation.isPending &&
                        statusMutation.variables.variantId === variant.id
                      }
                      onClick={() => {
                        void onToggleStatus(variant.id, !variant.isActive);
                      }}
                    >
                      {variant.isActive
                        ? t('merchant.products.variants.deactivate')
                        : t('merchant.products.variants.activate')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        void onDelete(variant.id);
                      }}
                    >
                      {t('merchant.common.delete')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Modal
        open={modalOpen}
        title={t('merchant.products.variants.add')}
        onClose={() => {
          setModalOpen(false);
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setModalOpen(false);
              }}
            >
              {t('merchant.common.cancel')}
            </Button>
            <Button type="submit" form="variant-form" loading={createMutation.isPending}>
              {t('merchant.categories.createAction')}
            </Button>
          </>
        }
      >
        {formError !== null ? (
          <p
            role="alert"
            className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical"
          >
            {formError}
          </p>
        ) : null}
        <VariantForm
          onSubmit={(values) => {
            void onCreate(values);
          }}
          isSubmitting={createMutation.isPending}
        />
      </Modal>
    </div>
  );
}
